"use client"
import type { ItemQuoteInput } from "@opensea/graphql"
import { graphql, useQuery } from "@opensea/graphql"
import {
  ImageStack,
  Spinner,
  Toast,
  ToastAvatar,
  ToastContent,
  ToastErrorIcon,
  ToastSide,
  ToastSuccessIcon,
  ToastTitle,
  useToasts,
} from "@opensea/ui-kit"
import { Sell } from "@opensea/ui-kit/icons"
import { omit } from "es-toolkit"
import { useEffect, useMemo } from "react"
import { useTranslations } from "use-intl"
import { Avatar } from "../../../components/Avatar"
import { useRenderImage } from "../../../providers/RenderImageProvider"
import type { TransactionIdentifier } from "../../../stores/currentTransactionStore"
import { FlowActions } from "../../../stores/flowStore"
import type { FlowStateVariants } from "../../flow-state"

const FETCH_INTERVAL = 500

type AcceptOfferToastProps = {
  transactionIdentifiers: TransactionIdentifier[]
  itemQuotes: ItemQuoteInput[]
  itemImageMap: Record<string, string | null>
  flow?: FlowStateVariants
}

const query = graphql(
  `
    query AcceptOfferToastStatusQuery(
      $transactionIdentifiers: [TransactionIdentifier!]!
      $itemQuotes: [ItemQuoteInput!]!
    ) {
      buyReceipt(
        transactionIdentifiers: $transactionIdentifiers
        itemQuotes: $itemQuotes
      ) {
        status
        itemReceipts {
          __typename
          item {
            id
          }
        }
        failedItemReceipts {
          __typename
          item {
            id
          }
        }
      }
    }
  `,
)

export function AcceptOfferToast({
  itemQuotes = [],
  transactionIdentifiers,
  itemImageMap,
  flow,
}: AcceptOfferToastProps) {
  const t = useTranslations("wallet.AcceptOfferToast")
  const { renderImage } = useRenderImage()
  const { removeToast } = useToasts()

  const [result, reexecuteQuery] = useQuery({
    query,
    variables: {
      transactionIdentifiers: transactionIdentifiers.map(txn => ({
        chain: txn.chain,
        transactionHash: txn.hash,
      })),
      itemQuotes: itemQuotes.map(quote =>
        omit(quote, ["orderId"]),
      ) as ItemQuoteInput[],
    },
    pause: true,
  })
  const receipt = result.data?.buyReceipt

  useEffect(
    function callOnSuccessWhenComplete() {
      if (receipt?.status === "SUCCESS" && flow) {
        FlowActions.onSuccess(flow)
      }
    },
    [receipt?.status, flow],
  )

  useEffect(
    function pollForReceipt() {
      if (result.fetching) {
        return
      }
      const status = result.data?.buyReceipt.status
      if (status !== undefined && status !== "PENDING") {
        return
      }
      if (transactionIdentifiers.length === 0) {
        return
      }

      const timerId = setTimeout(() => {
        if (itemQuotes.length) {
          reexecuteQuery({ requestPolicy: "network-only" })
        }
      }, FETCH_INTERVAL)

      return () => clearTimeout(timerId)
    },
    [
      result.fetching,
      reexecuteQuery,
      result.data?.buyReceipt,
      itemQuotes.length,
      transactionIdentifiers.length,
    ],
  )

  useEffect(
    function removeToastWhenComplete() {
      let timerId: ReturnType<typeof setTimeout>
      if (receipt && receipt.status !== "PENDING") {
        timerId = setTimeout(
          removeToast,
          receipt.status === "FAILED" ? 4000 : 1500,
        )
      }

      return () => clearTimeout(timerId)
    },
    [receipt, removeToast],
  )

  const imageUrls = useMemo(() => {
    switch (receipt?.status) {
      case "SUCCESS":
      case "PARTIAL_SUCCESS":
        return receipt.itemReceipts.map(item =>
          item.item ? itemImageMap[item.item.id] : null,
        )
      case "FAILED":
        return receipt.failedItemReceipts.map(item =>
          item.item ? itemImageMap[item.item.id] : null,
        )
      default:
        return Object.values(itemImageMap)
    }
  }, [receipt, itemImageMap])

  const icon = useMemo(() => {
    switch (receipt?.status) {
      case "SUCCESS":
      case "PARTIAL_SUCCESS":
        return <ToastSuccessIcon />
      case "FAILED":
        return <ToastErrorIcon />
      case "PENDING":
      case undefined:
        return <Spinner size="sm" />
    }
  }, [receipt?.status])

  const badge = (
    <Avatar.Badge>
      <Sell size={12} />
    </Avatar.Badge>
  )

  const title = useMemo(() => {
    switch (receipt?.status) {
      case "SUCCESS":
      case "PARTIAL_SUCCESS":
        return t("successTitle", { count: imageUrls.length })
      case "FAILED":
        return t("failedTitle")
      default:
        return t("pendingTitle")
    }
  }, [imageUrls.length, receipt?.status, t])

  return (
    <Toast>
      {imageUrls.length === 1 ? (
        <ToastAvatar
          badge={badge}
          renderImage={renderImage}
          src={imageUrls[0] || ""}
        />
      ) : (
        <ImageStack
          overrides={{
            Avatar: {
              position: {
                center: {
                  className: "rounded",
                  renderImage,
                  badge,
                },
                right: {
                  className: "rounded",
                },
              },
            },
          }}
          size={44}
          srcs={imageUrls.slice(0, 2).map(imageUrl => imageUrl || "")}
        />
      )}
      <ToastContent>
        <ToastTitle>{title}</ToastTitle>
      </ToastContent>
      <ToastSide>{icon}</ToastSide>
    </Toast>
  )
}
